/**
 * A score with its own population.
 *
 * It renders the PAIR or nothing. 4.75 on 8 reviews and 4.7 on 14,896 are not
 * comparable facts, and a score shown without its count invites exactly that
 * comparison — so a missing count is an absence with a reason, not a score on
 * its own.
 */
"use client";
import type { ReactElement } from "react";
import { formatCount } from "./cresultcard.format";
import {
  CRC_ACCENT,
  CRC_FIGURE_SIZE,
  CRC_NUMERIC_STYLE,
  CRC_TEXT,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";
import {
  CresultcardFigureScale,
  CresultcardRatingPopulation,
} from "./cresultcard.types";

const STAR_PATH =
  "M12 3.2l2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.7l6.1-.9z";
const SCORE_DECIMALS = 1;
const STAR_SIZE_RATIO = 0.82;

interface CresultcardRatingProps {
  readonly value?: number;
  readonly count?: number;
  readonly scale: CresultcardFigureScale;
  /**
   * Whether the population may be absent. It defaults to `Required`, which is
   * the pair-or-nothing rule this component was built on and the reason it
   * exists — but a lane that genuinely publishes a score and no count at all
   * could not use the component, and rendering nothing for a rating the vendor
   * did publish is its own fabrication. Declaring `Optional` is the lane saying
   * "the count is not withheld, it does not exist".
   */
  readonly population?: CresultcardRatingPopulation;
}

export function CresultcardRating({
  value,
  count,
  scale,
  population = CresultcardRatingPopulation.Required,
}: CresultcardRatingProps): ReactElement | null {
  const size = CRC_FIGURE_SIZE[scale];
  const populationMissing =
    count == null && population === CresultcardRatingPopulation.Optional;

  if (value == null || (count == null && !populationMissing)) return null;

  const starSize = Math.round(size * STAR_SIZE_RATIO);

  return (
    <span
      data-el="Cresultcard/Rating/Root"
      style={{
        ...CRC_NUMERIC_STYLE,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        minWidth: 0,
      }}
    >
      <svg
        aria-hidden
        height={starSize}
        viewBox="0 0 24 24"
        width={starSize}
      >
        <path d={STAR_PATH} fill={CRC_ACCENT} opacity={0.9} />
      </svg>
      <span
        style={{
          fontSize: size,
          fontWeight: 740,
          lineHeight: 1,
          color: CRC_TEXT,
        }}
      >
        {value.toFixed(SCORE_DECIMALS)}
      </span>
      {count != null && (
        <span
          style={{
            fontSize: Math.max(9, Math.round(size * 0.62)),
            fontWeight: 540,
            lineHeight: 1,
            color: CRC_TEXT_FAINT,
          }}
        >
          {formatCount(count)}
        </span>
      )}
    </span>
  );
}
